'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { breakpoint, m, p } from '@nib/layout';
import { TickSystemIcon } from '@nib/icons';
import { copyFontFamily, copyColor, formInputBackgroundColor, formBorderColor, formSelectedToggleColor, colorWhite, standardFocusStyleDeclarations, token } from '@nib-components/theme';
const disabledCursor = 'not-allowed';
const disabledOpacity = 0.5;
const validFormModes = ['light', 'white'];
const validToggleTypes = ['checkbox', 'radio'];
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const Wrapper = styled(_ref => {
  let {
    isCompact,
    inline,
    ...rest
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    ...rest
  });
})`
  position: relative;
`;
Wrapper.displayName = 'ToggleInputWrapper';
/* eslint-disable */
const Label = styled(_ref2 => {
  let {
    type,
    hideInput,
    isCompact,
    boldLabel,
    formMode,
    ...rest
  } = _ref2;
  return /*#__PURE__*/_jsx("label", {
    ...rest
  });
})`
  ${p({
  xs: 2,
  mini: 4
})};
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.isCompact ? token('common.dimension.spacing.2', '0.5rem') : token('common.dimension.spacing.3', '0.75rem')};
  align-items: center;
  position: relative;
  text-align: start;
  ${breakpoint('mini')`
    flex-wrap: nowrap;
  `}
  ${breakpoint('sm')`
    display: inline-flex;
  `}
  background-color: var(--inputColorBg, ${formInputBackgroundColor});
  font-family: ${token('theme.typography.copy.fontFamily', copyFontFamily)};
  font-weight: ${_ref3 => {
  let {
    boldLabel
  } = _ref3;
  return boldLabel ? 700 : 400;
}};
  line-height: 1.5;
  word-break: break-word;
  hyphens: auto;
  color: var(--themeColorFg, ${copyColor});
  box-shadow: inset 0 0 0 2px var(--themeColorBorderGentle, ${formBorderColor});
  cursor: pointer;
  transition: background-color 0.2s;

  ${breakpoint('mini')`
    hyphens: none;  
  `}

  ${_ref4 => {
  let {
    disabled
  } = _ref4;
  return disabled && css`
      opacity: ${disabledOpacity};
      cursor: ${disabledCursor};
    `;
}};

  ${_ref5 => {
  let {
    isCompact
  } = _ref5;
  return isCompact && css`
      &&& {
        ${p(2)};
        font-size: 0.875rem;
      }
    `;
}};

  ${_ref6 => {
  let {
    selected
  } = _ref6;
  return selected && css`
      box-shadow: inset 0 0 0 2px var(--themeColorBorderSelected, ${formSelectedToggleColor});
    `;
}};

  ${_ref7 => {
  let {
    selected,
    hideInput
  } = _ref7;
  return selected && hideInput && css`
      color: var(--themeColorFgSelected, ${formSelectedToggleColor});
      background-color: var(--inputColorBg, ${colorWhite});
    `;
}};
`;
/* eslint-enable */
Label.displayName = 'ToggleInputLabel';
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const Input = styled(_ref8 => {
  let {
    valid,
    validated,
    isCompact,
    isEmptyAndOptional,
    ...rest
  } = _ref8;
  return /*#__PURE__*/_jsx("input", {
    ...rest
  });
})`
  ${m(0)};
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  cursor: pointer;

  /* a11y hidden input */
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px 1px 1px 1px);
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;

  &:focus-visible + ${Label} {
    ${standardFocusStyleDeclarations};
    position: relative;
    z-index: 1;
  }
`;
Input.displayName = 'ToggleInputInput';
const Subtitle = styled.span`
  display: block;
  font-weight: 400;
`;
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const IndicatorWrapper = styled(_ref9 => {
  let {
    isCompact,
    type,
    ...rest
  } = _ref9;
  return /*#__PURE__*/_jsx("div", {
    ...rest
  });
})`
  flex-shrink: 0;
  display: grid;
  place-content: center;

  > * {
    grid-area: 1 / 1;
  }

  --indicator-size: 2rem;
  --indicator-radius: 2px;
  ${_ref10 => {
  let {
    isCompact
  } = _ref10;
  return isCompact && `
    --indicator-size: 1.5rem;
  `;
}};
  width: var(--indicator-size);
  height: var(--indicator-size);
  box-shadow: inset 0 0 0 2px var(--inputColorBorder, ${formBorderColor});

  /* Type prop based */
  ${_ref11 => {
  let {
    type
  } = _ref11;
  return type === 'radio' && `--indicator-radius: 50%;
  `;
}};
  border-radius: var(--indicator-radius);
`;
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const StyledTick = styled(_ref12 => {
  let {
    isCompact,
    ...rest
  } = _ref12;
  return /*#__PURE__*/_jsx(TickSystemIcon, {
    ...rest
  });
})`
  box-sizing: border-box;
  background-color: transparent;
  color: var(--themeColorFgSelected, ${formSelectedToggleColor});

  /* IE compatibility (doesn't render correctly without a transparent background-color and no height) */
  @supports (height: auto) {
    height: auto;
    background-color: var(--inputColorBg, ${colorWhite});
  }
  width: ${_ref13 => {
  let {
    isCompact
  } = _ref13;
  return isCompact ? '1rem' : '1.25rem';
}};
`;
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const Dot = styled(_ref14 => {
  let {
    isCompact,
    type,
    ...rest
  } = _ref14;
  return /*#__PURE__*/_jsx("div", {
    ...rest
  });
})`
  --dot-size: 1rem;
  background-color: var(--themeColorFgSelected, ${formSelectedToggleColor});
  ${_ref15 => {
  let {
    isCompact
  } = _ref15;
  return isCompact && `
    --dot-size: 0.75rem;
  `;
}};
  border-radius: 50%;
  width: var(--dot-size);
  height: var(--dot-size);
`;
const ToggleInput = props => {
  // eslint-disable-next-line  @typescript-eslint/no-unused-vars
  const {
    type,
    id,
    label,
    subtitle,
    selected,
    disabled,
    className,
    formMode,
    inline = true,
    isCompact,
    hideInput = false,
    boldLabel,
    formType,
    fullstory,
    formfieldtype,
    ...otherProps
  } = props;
  return /*#__PURE__*/_jsxs(Wrapper, {
    inline: inline,
    isCompact: isCompact,
    "data-mesh-component": "TOGGLE-INPUT",
    children: [/*#__PURE__*/_jsx(Input, {
      onChange: () => {},
      ...otherProps,
      id: id,
      type: type,
      checked: selected,
      disabled: disabled,
      isCompact: isCompact
    }), /*#__PURE__*/_jsxs(Label, {
      htmlFor: id,
      disabled: disabled,
      selected: selected,
      type: type,
      className: className,
      formMode: formMode,
      isCompact: isCompact,
      hideInput: hideInput,
      boldLabel: boldLabel || !!subtitle,
      children: [!hideInput && /*#__PURE__*/_jsxs(IndicatorWrapper, {
        isCompact: isCompact,
        type: type,
        children: [type === 'checkbox' && selected && /*#__PURE__*/_jsx(StyledTick, {
          isCompact: isCompact
        }), type === 'radio' && selected && /*#__PURE__*/_jsx(Dot, {
          isCompact: isCompact
        })]
      }), /*#__PURE__*/_jsxs("div", {
        children: [label, subtitle && /*#__PURE__*/_jsx(Subtitle, {
          children: subtitle
        })]
      })]
    })]
  });
};
ToggleInput.displayName = 'ToggleInput';
ToggleInput.Input = Input;
ToggleInput.Label = Label;
ToggleInput.Wrapper = Wrapper;
export default ToggleInput;